import React from 'react';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { useQuery, useQueryClient } from 'react-query';

const FIVE_SECONDS = 5000;

const createNotification = (params) => _.assign({
  autohide: false,
  delay: FIVE_SECONDS,
  id: uuid(),
  title: 'Info',
  category: 'info',
  details: 'Notification Details',
}, params);

/**
 * Utility hook for interacting with the toast notification system.
 *
 * Expected usage is:
 *
 * const { notify } = useNotificationToaster();
 * performActionForExampleAnApiCall().then(
 *   () => notify({ category: 'success', title: '🥳', details: 'yay!' }),
 *   () => notify({ category: 'error', title: '💣', details: 'oh no!' })
 * );
 *
 * `notifyMultiple` can be used to render an array of distinct notifications.
 * Use with caution to avoid overwhelming users!
 *
 * Main parameters when creating notifications:
 *  - title: The header of the notification. It can be a string, a rendered component, or a render function.
 *  - category: The category of the notification (info/warning/error/success).
 *  - details: The body of the notification. It can be a string, a rendered component, or a render function.
 *
 * Extra parameters when creating notifications:
 *  - autohide: defaults to false. true/false (set the notification to disappear automatically after a given delay).
 *  - delay: defaults to 5000 ms, controls the time (in ms) the notification will stay on screen before disappearing. it has no effect if autohide is not true.
 *  - bg: defaults to be set depending on the category. avoid providing this value! Can be used to force override the color of a notification, regardless of category.
 *
 * @returns \{ notify, dismiss, notifyMultiple, dismissMultiple, dismissAll \}
 */
export const useNotificationToaster = () => {
  const queryClient = useQueryClient();

  const notifyMultiple = React.useCallback((toasts) => {
    queryClient.setQueryData(['toaster-notifications'], (existing) => _.assign({}, existing, {
      toasts: _.concat(existing?.toasts || [], _.map(toasts, createNotification)),
    }));
  }, [queryClient]);

  const notify = React.useCallback(({
    id, title, category, details,
  } = {}) => {
    notifyMultiple([{
      id, title, category, details,
    }]);
  }, [notifyMultiple]);

  const dismissMultiple = React.useCallback((toasts) => {
    queryClient.setQueryData(['toaster-notifications'], (existing) => _.assign({}, existing, { toasts: _.reject(existing?.toasts, ({ id } = {}) => _.some(toasts, { id })) }));
  }, [queryClient]);

  const dismiss = React.useCallback(({ id } = {}) => {
    dismissMultiple([{ id }]);
  }, [dismissMultiple]);

  const dismissAll = React.useCallback(() => {
    queryClient.setQueryData(['toaster-notifications'], (existing) => _.assign({}, existing, { toasts: [] }));
  }, [queryClient]);

  return React.useMemo(() => ({
    notify, notifyMultiple, dismiss, dismissMultiple, dismissAll,
  }), [
    notify, dismiss, notifyMultiple, dismissMultiple, dismissAll,
  ]);
};

export const useQueryToasterNotifications = () => (
  useQuery(['toaster-notifications'], () => Promise.resolve({ toasts: [] }))
);
